import React, { useEffect, useRef } from "react";
import { canAnimate, clamp, ease } from "./motion";
import { BOX, FRAMES, KINDS, drawPlant, drawWorkstation, taskFor, workstationSprite } from "./workstation";

// What the workers just finished, told as a scale story: a founder's first worker, then a team,
// a company running its operations, an organization, and then everything else.
const STAGE_WORK = {
  "Your first worker": [
    "SDR · Meeting booked",
    "Recruiter · Offer sent",
    "Executive assistant · Inbox cleared",
    "Copywriter · Launch email drafted",
    "Engineer · Bug fixed",
    "Market researcher · Survey analysed",
  ],
  "A team": [
    "Sales rep · Deal closed",
    "Social media manager · Post scheduled",
    "SEO specialist · Page ranked",
    "Onboarding guide · Customer live",
    "Support agent · Ticket resolved",
    "Customer success · Churn prevented",
    "Bookkeeper · Receipts matched",
    "QA tester · Release verified",
    "DevOps engineer · Deploy shipped",
    "Product manager · Roadmap updated",
    "Ecommerce manager · Catalogue updated",
    "Order ops · Refund processed",
  ],
  "A company": [
    "Accountant · Books closed",
    "Payroll specialist · Payroll run",
    "People ops · New hire onboarded",
    "HR generalist · Policy answered",
    "IT help desk · Laptop set up",
    "IT ops · Access provisioned",
    "IT ops · Password reset",
    "Project manager · Standup run",
    "RevOps · Pipeline cleaned",
    "Financial analyst · Forecast updated",
    "Data analyst · Dashboard refreshed",
    "Account manager · Renewal signed",
    "Accounts payable · Invoices matched",
    "Accounts receivable · Payment collected",
    "Back office · Orders reconciled",
    "Data entry · Records updated",
    "Procurement · Purchase order approved",
    "Support ops · Macros updated",
    "Marketplace ops · Supply onboarded",
    "Marketplace ops · Listings verified",
    "Marketplace ops · Buyer and seller matched",
    "Payouts · Sellers paid",
    "Logistics coordinator · Shipment tracked",
    "Dispatcher · Route optimised",
  ],
  "An organization": [
    "BPO team · Queue cleared",
    "Shared services · SLA met",
    "Vendor manager · Vendor onboarded",
    "Transition lead · Process insourced",
    "Trust & safety · Listing reviewed",
    "Content moderator · Report actioned",
    "Fraud analyst · Chargeback prevented",
    "KYC analyst · Identity verified",
    "Risk analyst · Account flagged",
    "Dispute resolver · Dispute resolved",
    "Claims handler · Claim settled",
    "Clinic coordinator · Appointment set",
    "Loan processor · Application approved",
    "Compliance officer · Audit prepared",
    "Paralegal · Case file prepared",
    "Security analyst · Alert triaged",
    "Site reliability · Incident resolved",
    "IT ops · Patch rolled out",
    "IT asset manager · Licences reclaimed",
    "FinOps · Cloud spend cut",
    "Buyer · Supplier negotiated",
    "Supply planner · Stock rebalanced",
    "Research assistant · Sources summarised",
    "Receptionist · Call answered",
  ],
  "Without limits": [
    "Teacher · Lesson delivered",
    "Tutor · Homework reviewed",
    "Translator · Contract translated",
    "Travel agent · Trip booked",
    "Real estate agent · Viewing booked",
    "Property manager · Repair scheduled",
    "Event planner · Venue secured",
    "Designer · Mockups shipped",
    "Video editor · Trailer cut",
    "Editor · Article polished",
    "Fact-checker · Claims verified",
    "Community manager · Question answered",
    "Trainer · Course published",
    "Coach · Session prepared",
    "Admissions officer · Application reviewed",
    "Grant writer · Proposal submitted",
    "Fundraiser · Donor thanked",
    "Tax preparer · Return filed",
    "Concierge · Table reserved",
    "Pricing analyst · Prices tuned",
    "Librarian · Archive catalogued",
  ],
};
const WEIGHTS = [0.17, 0.2, 0.13, 0.16, 0.12, 0.12, 0.1];
const PERIOD = 1300;
const hash = (i, j, salt = 0) => {
  let h = Math.imul(i | 0, 374761393) ^ Math.imul(j | 0, 668265263) ^ Math.imul(salt + 1, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};
const mod = (n, m) => ((n % m) + m) % m;
const aisle = (i, j) => mod(i, 5) === 2 || mod(j, 5) === 2;
const occupied = (i, j) => (i === 0 && j === 0) || (!aisle(i, j) && hash(i, j) < 0.66);
const plant = (i, j) => aisle(i, j) && !(mod(i, 5) === 2 && mod(j, 5) === 2) && hash(i, j, 9) < 0.07;
const distance = (i, j) => (i === 0 && j === 0 ? 0 : Math.hypot(i, j) + hash(i, j, 1) * 1.4);
const workerId = (i, j) => (i === 0 && j === 0 ? "0001" : String(1000 + Math.floor(hash(i, j, 3) * 8999)));
function kindOf(i, j) {
  if (i === 0 && j === 0) return "triple";
  let r = hash(i, j, 7);
  for (let k = 0; k < KINDS.length; k += 1) {
    r -= WEIGHTS[k];
    if (r <= 0) return KINDS[k];
  }
  return KINDS[0];
}
const flipOf = (i, j) => (i === 0 && j === 0 ? false : hash(i, j, 8) < 0.5);

function count(p, r) {
  if (p < 0.05) return 1;
  return Math.round(Math.max(1.3 * r * r, Math.pow(10, 6.2 * Math.pow(p, 1.35))));
}
function stage(n) {
  if (n <= 1) return "Your first worker";
  if (n < 25) return "A team";
  if (n < 2000) return "A company";
  if (n < 200000) return "An organization";
  return "Without limits";
}

// Start with one digital humanoid worker at its desk. Scroll, and it replicates while the camera
// pulls back: a team, a company, then a workforce on calls, laptops, tablets and screens,
// running past every edge of the frame.
export default function WorkerScale() {
  const track = useRef(null);
  const host = useRef(null);
  const canvasRef = useRef(null);
  const countRef = useRef(null);
  const stageRef = useRef(null);
  useEffect(() => {
    const box = host.current;
    const rail = track.current;
    const canvas = canvasRef.current;
    if (!box || !rail || !canvas || !window.matchMedia) return undefined;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return undefined;
    const animate = canAnimate();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const spriteDpr = Math.min(dpr, 1.5);
    const cache = new Map();
    const sprite = (tier, kind, flip, frame) => {
      const key = tier + kind + flip + frame;
      let img = cache.get(key);
      if (!img) {
        img = kind === "plant" ? plantSprite(tier) : workstationSprite(kind, flip, frame, tier, spriteDpr);
        cache.set(key, img);
      }
      return img;
    };
    const plantSprite = (tier) => {
      const c = document.createElement("canvas");
      const scale = tier * spriteDpr;
      c.width = Math.ceil(BOX.width * scale);
      c.height = Math.ceil(BOX.height * scale);
      const g = c.getContext("2d");
      g.translate(-BOX.left * scale, -BOX.top * scale);
      drawPlant(g, scale);
      return c;
    };
    let w = 0;
    let h = 0;
    let aMax = 64;
    let aMin = 5;
    let shown = animate ? 0 : 1;
    let raf = 0;
    let visible = false;
    let hover = null;
    let chips = [];
    let packets = [];
    let lastChip = 0;
    const told = {};
    const nextWork = (label) => {
      const list = STAGE_WORK[label];
      told[label] = (told[label] ?? -1) + 1;
      return list[told[label] % list.length];
    };
    let lastPacket = 0;
    const born = new Map();
    let lastR = 0;
    const hops = new Map();
    const resize = () => {
      const rect = box.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      const phone = w < 700;
      aMax = phone ? 72 : 124;
      aMin = phone ? 4.6 : 7;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const view = () => {
      const z = ease(clamp((shown - 0.08) / 0.86));
      const a = aMax * Math.pow(aMin / aMax, z);
      const r = shown < 0.05 ? 0.4 : 0.4 + Math.pow((shown - 0.05) / 0.95, 1.7) * 150;
      return { a, r, z, cx: w / 2 - 0.2 * a * (1 - z), cy: h * (0.72 - 0.22 * z) };
    };
    const frame = (t) => {
      raf = visible ? requestAnimationFrame(frame) : 0;
      // The scene is pinned while its track scrolls past; progress is how far through the track you are.
      const railRect = rail.getBoundingClientRect();
      const pin = parseFloat(getComputedStyle(box).top) || 0;
      const travel = railRect.height - box.offsetHeight;
      const target = animate && travel > 40 ? clamp((pin - railRect.top) / travel) : 1;
      shown += (target - shown) * (animate ? 0.07 : 1);
      if (Math.abs(target - shown) < 0.0005) shown = target;
      const { a, r, cx, cy } = view();
      // The stage on screen decides which work the cards talk about.
      const n = count(shown, r);
      const label = shown > 0.99 ? "Without limits" : stage(n);
      const tier = a >= 46 ? "live" : a >= 24 ? 46 : 24;
      const detail = clamp((a - 12) / 2);
      if (r < lastR - 0.5) born.clear();
      lastR = r;
      const phase = (t / PERIOD) % 1;
      ctx.clearRect(0, 0, w, h);
      // Floor: an endless isometric grid, drawn along the tile edges
      const floor = new Path2D();
      const c1 = cy + 0.5 * cx;
      const c2 = cy - 0.5 * cx;
      for (let n = Math.floor(-c1 / a) - 1; n <= Math.ceil((h + 0.5 * w - c1) / a) + 1; n += 1) {
        const y0 = c1 + (n - 0.5) * a;
        floor.moveTo(0, y0);
        floor.lineTo(w, y0 - 0.5 * w);
      }
      for (let n = Math.floor((-0.5 * w - c2) / a) - 1; n <= Math.ceil((h - c2) / a) + 1; n += 1) {
        const y0 = c2 + (n + 0.5) * a;
        floor.moveTo(0, y0);
        floor.lineTo(w, y0 + 0.5 * w);
      }
      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(25,25,25,${0.03 + 0.03 * detail})`;
      ctx.stroke(floor);
      const sMin = Math.ceil((-0.6 * a - cy) / (a / 2));
      const sMax = Math.floor((h + 2.2 * a - cy) / (a / 2));
      const kMin = Math.floor((-1.4 * a - cx) / a);
      const kMax = Math.ceil((w + 1.4 * a - cx) / a);
      const desks = new Path2D();
      const screensDim = new Path2D();
      const screensLit = new Path2D();
      const heads = [new Path2D(), new Path2D(), new Path2D()];
      const bodies = new Path2D();
      const flashes = [];
      const seen = [];
      const drawSprite = (img, x, y) => ctx.drawImage(img, x + BOX.left * a, y + BOX.top * a, BOX.width * a, BOX.height * a);
      for (let s = sMin; s <= sMax; s += 1) {
        let k = kMin;
        if (mod(k - s, 2)) k += 1;
        for (; k <= kMax; k += 2) {
          const i = (s + k) / 2;
          const j = (s - k) / 2;
          const x = cx + (i - j) * a;
          let y = cy + (i + j) * (a / 2);
          if (!occupied(i, j)) {
            if (detail > 0 && plant(i, j) && distance(i, j) <= r) {
              ctx.globalAlpha = detail;
              if (tier === "live") {
                ctx.save();
                ctx.translate(x, y);
                drawPlant(ctx, a);
                ctx.restore();
              } else drawSprite(sprite(tier, "plant", false, 0), x, y);
              ctx.globalAlpha = 1;
            }
            continue;
          }
          const d = distance(i, j);
          if (d > r) continue;
          const key = i * 100003 + j;
          // Only workers at the growth front are still materialising.
          let age = 5000;
          if (detail > 0 && d > r - 3) {
            let at = born.get(key);
            if (at === undefined) {
              at = animate ? t : t - 5000;
              born.set(key, at);
            }
            age = t - at;
          }
          const hop = hops.size ? hops.get(key) : undefined;
          if (hop !== undefined) {
            const q = (t - hop) / 520;
            if (q >= 1) hops.delete(key);
            else y -= Math.sin(q * Math.PI) * a * 0.4;
          }
          const kind = kindOf(i, j);
          const flip = flipOf(i, j);
          const lounge = kind.charCodeAt(0) === 108;
          if ((a > 20 || hash(i, j, 12) < 0.08) && x > w * 0.08 && x < w * 0.92 && y > 0 && y < h + a)
            seen.push({ i, j, x, y, age, kind });
          if (detail < 1) {
            // Far away: a floor of desks and glowing screens, with work rippling out from the first worker.
            const m = flip ? -1 : 1;
            const lit = Math.sin(d * 0.42 - t * 0.0045) > 0.4 || hash(i, j, 11) > 0.9;
            const layer = heads[y < h * 0.33 ? 0 : y < h * 0.66 ? 1 : 2];
            if (lounge) {
              desks.rect(x - 0.3 * a, y - 0.62 * a, 0.6 * a, 0.36 * a);
              bodies.rect(x - 0.13 * a, y - 1.02 * a, 0.26 * a, 0.42 * a);
              layer.rect(x - 0.1 * a, y - 1.26 * a, 0.2 * a, 0.22 * a);
              if (kind === "lounge-tablet") (lit ? screensLit : screensDim).rect(x - 0.14 * a, y - 0.9 * a, 0.28 * a, 0.16 * a);
            } else {
              if (a < 8) desks.rect(x + (flip ? -0.34 : -0.92) * a, y - 1.0 * a, 1.26 * a, 0.5 * a);
              else {
                desks.moveTo(x + m * -0.04 * a, y - 1.05 * a);
                desks.lineTo(x + m * 0.34 * a, y - 0.86 * a);
                desks.lineTo(x + m * -0.54 * a, y - 0.42 * a);
                desks.lineTo(x + m * -0.92 * a, y - 0.61 * a);
                desks.closePath();
              }
              (lit ? screensLit : screensDim).rect(x + m * -0.4 * a - 0.18 * a, y - 1.42 * a, 0.36 * a, 0.4 * a);
              bodies.rect(x + m * 0.24 * a - 0.12 * a, y - 0.86 * a, 0.24 * a, 0.44 * a);
              layer.rect(x + m * 0.22 * a - 0.1 * a, y - 1.08 * a, 0.2 * a, 0.22 * a);
            }
          }
          if (detail > 0) {
            if (age < 700) flashes.push({ x, y, q: age / 700, i, j });
            const f = Math.floor((phase + hash(i, j, 2)) * FRAMES) % FRAMES;
            const paint = () => {
              if (tier === "live") {
                ctx.save();
                ctx.translate(x, y);
                if (flip) ctx.scale(-1, 1);
                drawWorkstation(ctx, a, kind, (phase + hash(i, j, 2)) % 1);
                ctx.restore();
              } else drawSprite(sprite(tier, kind, flip, f), x, y);
            };
            ctx.globalAlpha = detail;
            if (age < 700) {
              const q = ease(clamp(age / 700));
              const edge = y + 0.6 * a - (BOX.height - 0.1) * a * q;
              ctx.save();
              ctx.beginPath();
              ctx.rect(x + BOX.left * a, edge, BOX.width * a, BOX.height * a + a);
              ctx.clip();
              paint();
              ctx.restore();
              const beam = ctx.createLinearGradient(0, edge - a * 1.6, 0, edge);
              beam.addColorStop(0, "rgba(25,25,25,0)");
              beam.addColorStop(1, `rgba(25,25,25,${0.12 * (1 - q)})`);
              ctx.fillStyle = beam;
              ctx.fillRect(x - 0.9 * a, edge - a * 1.6, 1.8 * a, a * 1.6);
              ctx.fillStyle = `rgba(25,25,25,${0.75 * (1 - q)})`;
              ctx.fillRect(x - 0.9 * a, edge, 1.8 * a, 1.2);
            } else paint();
            ctx.globalAlpha = 1;
          }
        }
      }
      if (detail < 1) {
        ctx.globalAlpha = 1 - detail;
        ctx.fillStyle = "#ffffff";
        ctx.fill(desks);
        ctx.strokeStyle = "rgba(25,25,25,0.3)";
        ctx.lineWidth = a < 8 ? 0.5 : 0.7;
        ctx.stroke(desks);
        ctx.fillStyle = "rgba(25,25,25,0.26)";
        ctx.fill(screensDim);
        ctx.fillStyle = "#191919";
        ctx.fill(screensLit);
        ctx.fillStyle = "#e4e4e4";
        ctx.fill(bodies);
        ctx.strokeStyle = "rgba(25,25,25,0.4)";
        ctx.stroke(bodies);
        [0.45, 0.62, 0.8].forEach((alpha, n) => {
          ctx.fillStyle = `rgba(25,25,25,${alpha})`;
          ctx.fill(heads[n]);
        });
        ctx.globalAlpha = 1;
      }
      // Replication beams from the worker that spawned each new one
      if (detail > 0) {
        ctx.globalAlpha = detail;
        ctx.setLineDash([2, 4]);
        for (const f of flashes) {
          if (!f.i && !f.j) continue;
          const pi = Math.abs(f.i) >= Math.abs(f.j) ? f.i - Math.sign(f.i) : f.i;
          const pj = Math.abs(f.i) >= Math.abs(f.j) ? f.j : f.j - Math.sign(f.j);
          if (!occupied(pi, pj)) continue;
          const px = cx + (pi - pj) * a;
          const py = cy + (pi + pj) * (a / 2) - 1.1 * a;
          const qx = f.x;
          const qy = f.y - 1.1 * a;
          const q = clamp(f.q * 1.6);
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.quadraticCurveTo(
            (px + qx) / 2,
            Math.min(py, qy) - a * 0.8,
            px + (qx - px) * q,
            py + (qy - py) * q - Math.sin(q * Math.PI) * a * 0.5,
          );
          ctx.strokeStyle = `rgba(25,25,25,${0.5 * (1 - f.q)})`;
          ctx.lineWidth = 1.2;
          ctx.stroke();
        }
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
      }
      // Work moving between workers
      const ready = seen.filter((c) => c.age > 900);
      if (animate && ready.length > 3 && t - lastPacket > 240 && packets.length < 10) {
        lastPacket = t;
        const from = ready[Math.floor(Math.random() * ready.length)];
        const near = ready.filter((c) => c !== from && Math.abs(c.i - from.i) + Math.abs(c.j - from.j) < 9);
        const to = near[Math.floor(Math.random() * near.length)];
        if (to) packets.push({ from: [from.i, from.j], to: [to.i, to.j], born: t, life: 900 + Math.random() * 700 });
      }
      packets = packets.filter((p) => t - p.born < p.life + 450);
      ctx.fillStyle = "#191919";
      for (const p of packets) {
        const q = clamp((t - p.born) / p.life);
        const place = ([i, j]) => [cx + (i - j) * a, cy + (i + j) * (a / 2) - 1.35 * a];
        const [x1, y1] = place(p.from);
        const [x2, y2] = place(p.to);
        const mx = (x1 + x2) / 2;
        const my = Math.min(y1, y2) - 20 - Math.abs(x2 - x1) * 0.3;
        const at = (u) => [
          (1 - u) * (1 - u) * x1 + 2 * (1 - u) * u * mx + u * u * x2,
          (1 - u) * (1 - u) * y1 + 2 * (1 - u) * u * my + u * u * y2,
        ];
        ctx.beginPath();
        const tail = Math.max(0, q - 0.35);
        for (let k = 0; k <= 12; k += 1) {
          const [x, y] = at(tail + ((q - tail) * k) / 12);
          if (k) ctx.lineTo(x, y);
          else ctx.moveTo(x, y);
        }
        ctx.strokeStyle = "rgba(25,25,25,0.4)";
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
        const [hx, hy] = at(q);
        ctx.beginPath();
        if (q < 1) {
          ctx.arc(hx, hy, 2.2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.arc(x2, y2, 2 + clamp((t - p.born - p.life) / 450) * 10, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(25,25,25,${0.5 * (1 - clamp((t - p.born - p.life) / 450))})`;
          ctx.stroke();
        }
      }
      // What the workers just finished
      if (animate && ready.length && t - lastChip > (a > 20 ? 1000 : 520) && chips.length < (a > 20 ? 2 : 5)) {
        lastChip = t;
        const inside = ready.filter((c) => c.x > w * 0.12 && c.x < w * 0.88 && c.y - 1.8 * a > 40 && c.y < h - 10);
        const pick = inside[Math.floor(Math.random() * inside.length)];
        // Keep the cards clear of each other
        const crowded = (c) =>
          Math.abs(cx + (c.i - c.j) * a - pick.x) < 170 && Math.abs(cy + (c.i + c.j) * (a / 2) - pick.y) < 34;
        if (pick && !chips.some((c) => (c.i === pick.i && c.j === pick.j) || crowded(c)))
          chips.push({ i: pick.i, j: pick.j, text: nextWork(label), born: t });
      }
      chips = chips.filter((c) => t - c.born < 2600);
      const pill = (x, y, text, alpha, strong) => {
        ctx.font = `600 ${strong ? 12 : 11}px Manrope, Arial, sans-serif`;
        const pw = ctx.measureText(text).width + 30;
        const ph = strong ? 26 : 22;
        const px = clamp(x - pw / 2, 6, w - pw - 6);
        const py = y - ph;
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(px, py, pw, ph, ph / 2);
        else ctx.rect(px, py, pw, ph);
        ctx.fillStyle = strong ? "#191919" : "#ffffff";
        ctx.fill();
        ctx.strokeStyle = "rgba(25,25,25,0.16)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(px + 12, py + ph / 2, 5, 0, Math.PI * 2);
        ctx.fillStyle = strong ? "#ffffff" : "#191919";
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(px + 9.6, py + ph / 2);
        ctx.lineTo(px + 11.4, py + ph / 2 + 1.8);
        ctx.lineTo(px + 14.6, py + ph / 2 - 1.8);
        ctx.strokeStyle = strong ? "#191919" : "#ffffff";
        ctx.lineWidth = 1.4;
        ctx.stroke();
        ctx.fillStyle = strong ? "#ffffff" : "#191919";
        ctx.textBaseline = "middle";
        ctx.fillText(text, px + 22, py + ph / 2 + 0.5);
        ctx.globalAlpha = 1;
      };
      for (const c of chips) {
        const age = t - c.born;
        const alpha = clamp(age / 250) * clamp((2600 - age) / 400);
        const x = cx + (c.i - c.j) * a;
        const y = cy + (c.i + c.j) * (a / 2) - 1.75 * a - 4 - clamp(age / 2600) * 10;
        pill(x, y, c.text, alpha, false);
      }
      if (hover && animate) {
        let best = null;
        const di = (hover.x - cx) / a;
        const si = (hover.y + 0.6 * a - cy) / (a / 2);
        const i0 = Math.round((di + si) / 2);
        const j0 = Math.round((si - di) / 2);
        for (let i = i0 - 1; i <= i0 + 1; i += 1)
          for (let j = j0 - 1; j <= j0 + 1; j += 1) {
            if (!occupied(i, j) || distance(i, j) > r) continue;
            const x = cx + (i - j) * a;
            const y = cy + (i + j) * (a / 2);
            const d = Math.hypot(x - hover.x, y - 0.6 * a - hover.y);
            if (d < Math.max(22, a * 0.95) && (!best || d < best.d)) best = { i, j, x, y, d, kind: kindOf(i, j) };
          }
        hover.cell = best;
        if (best) {
          ctx.beginPath();
          ctx.moveTo(best.x, best.y - a / 2);
          ctx.lineTo(best.x + a, best.y);
          ctx.lineTo(best.x, best.y + a / 2);
          ctx.lineTo(best.x - a, best.y);
          ctx.closePath();
          ctx.strokeStyle = "#191919";
          ctx.lineWidth = 1.5;
          ctx.stroke();
          pill(
            best.x,
            best.y - Math.max(1.8 * a, 12) - 6,
            `Worker ${workerId(best.i, best.j)} · ${taskFor(best.kind, hash(best.i, best.j, 4))}`,
            1,
            true,
          );
        }
        box.classList.toggle("is-pointing", !!best);
      }
      if (countRef.current) countRef.current.textContent = shown > 0.99 ? "∞" : n.toLocaleString("en-US").padStart(3, "0");
      if (stageRef.current && stageRef.current.textContent !== label) stageRef.current.textContent = label;
      box.style.setProperty("--bm-scale-hint", (1 - clamp(shown / 0.08)).toFixed(2));
    };
    const onMove = (e) => {
      if (e.pointerType !== "mouse") return;
      const rect = canvas.getBoundingClientRect();
      hover = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onLeave = () => {
      hover = null;
      box.classList.remove("is-pointing");
    };
    const onClick = () => {
      const cell = hover?.cell;
      if (!cell) return;
      const now = performance.now();
      hops.set(cell.i * 100003 + cell.j, now);
      chips = chips.filter((c) => c.i !== cell.i || c.j !== cell.j);
      chips.push({ i: cell.i, j: cell.j, text: "On it.", born: now });
    };
    resize();
    visible = true;
    frame(performance.now());
    visible = false;
    const observer = window.IntersectionObserver
      ? new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          if (visible && !raf) raf = requestAnimationFrame(frame);
        })
      : null;
    observer?.observe(box);
    const resizer = window.ResizeObserver
      ? new ResizeObserver(() => {
          if (Math.abs(box.getBoundingClientRect().width - w) > 2) resize();
        })
      : null;
    resizer?.observe(box);
    if (animate) {
      box.addEventListener("pointermove", onMove);
      box.addEventListener("pointerleave", onLeave);
      box.addEventListener("click", onClick);
    }
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      resizer?.disconnect();
      box.removeEventListener("pointermove", onMove);
      box.removeEventListener("pointerleave", onLeave);
      box.removeEventListener("click", onClick);
    };
  }, []);
  return (
    <div ref={track} className="bm-scale-track">
      <div ref={host} className="bm-scale" aria-hidden="true">
        <canvas ref={canvasRef} />
        <div className="bm-scale-counter">
          <span className="brand-caption">Workers online</span>
          <b ref={countRef}>001</b>
          <span ref={stageRef} className="bm-scale-stage">
            Your first worker
          </span>
        </div>
        <span className="bm-scale-hint">Scroll to spin up workers</span>
      </div>
    </div>
  );
}
